import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  get_all_skills_action,
  search_candidate_kanban_board_user_action,
  user_meta_data_action,
} from "../../../redux/_actions";

function useCandidateAppliedJobs(props) {
  const [loading, setLoading] = useState(false);
  const [allJobs, setAllJobs] = useState([]);
  //PAGINATION
  const [nextPage, setNextPage] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [lastElement, setLastElement] = useState(null);
  const [newJobsLoading, setNewJobsLoading] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [jobSearch, setJobSearch] = useState([]);
  const [candidateSelectedSkill, setCandidateSelectedSkill] = useState([]);
  const [candidateSelectedEmployment, setCandidateSelectedEmployment] =
    useState([]);
  const [candidateSelectedExperience, setCandidateSelectedExperience] =
    useState([]);
  const [candidateSelectedCountry, setCandidateSelectedCountry] = useState([]);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [skillsTagsAdded, setSkillsTagsAdded] = useState([]);
  const [skillsListLoading, setSkillsListLoading] = useState(false);
  const [candidateSelectedInterviewStage, setCandidateSelectedInterviewStage] =
    useState("");
  const [showSkillSearch, setShowSkillSearch] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const searchSkillInputRef = useRef(null);

  const { userMetaData } = useSelector((state) => state.user_meta_data_reducer);

  const { candidateKanbanSearchData } = useSelector(
    (state) => state.search_candidate_kanban_board_user_reducer
  );

  const { allSkillsData } = useSelector(
    (state) => state.get_all_skills_reducer
  );

  useEffect(() => {
    setLoading(true);
    getUserMeta();
  }, []);

  useEffect(() => {
    setLoading(true);
    callSkillsApi();
  }, [userMetaData]);

  useEffect(() => {
    if (nextPage !== null) {
      setNewJobsLoading(true);
      getAllAppliedJobs();
    }
  }, [pageNum]);

  useEffect(() => {
    getAllAppliedJobs();
  }, [
    candidateSelectedSkill,
    candidateSelectedEmployment,
    candidateSelectedExperience,
    candidateSelectedCountry,
    candidateSelectedInterviewStage,
    searchInputValue,
  ]);

  useEffect(() => {
    if (allSkillsData?.data) {
      setSkillsTagsAdded(allSkillsData?.data);
    }
  }, [allSkillsData]);

  useEffect(() => {
    if (candidateKanbanSearchData?.data) {
      setAllJobs(candidateKanbanSearchData?.data);
    }
  }, [candidateKanbanSearchData]);

  //infinite scroll pagination
  useEffect(() => {
    const currentElement = lastElement;
    const currentObserver = observer.current;

    if (currentElement) {
      currentObserver.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement);
      }
    };
  }, [lastElement]);

  const observer = useRef(
    new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting) {
        setPageNum((no) => no + 1);
      }
    })
  );

  const getUserMeta = async () => {
    await dispatch(user_meta_data_action());
  };

  const callSkillsApi = async (value) => {
    setSkillsListLoading(true);
    const data = { index: "skill", search_key: "", search: value ? value : "" };
    await dispatch(get_all_skills_action(data));
    setSkillsListLoading(false);
  };

  const onViewJobDetails = (id, index) => {
    navigate("/applied-jobs-expanded", {
      state: {
        job_id: id,
        index: index,
      },
    });
  };

  const onSearch = () => {
    if (showSearch) {
      if (searchInputRef.current) {
        searchInputRef.current.value = "";
      }
    }
    setShowSearch((showSearch) => !showSearch);
    setSearchInputValue("");
    getAllAppliedJobs();
  };

  const onSkillSearch = () => {
    if (showSkillSearch) {
      if (searchSkillInputRef.current) {
        searchSkillInputRef.current.value = "";
      }
    }
    setShowSkillSearch((showSkillSearch) => !showSkillSearch);
    callSkillsApi();
  };

  const getAllAppliedJobs = async () => {
    const data = {
      index: "candidate_job_application",
      search_key: "",
      search: searchInputValue ? searchInputValue : "",
      employment_type:
        Array.isArray(candidateSelectedEmployment) &&
        candidateSelectedEmployment.length
          ? candidateSelectedEmployment
          : [],
      experience:
        Array.isArray(candidateSelectedExperience) &&
        candidateSelectedExperience.length
          ? candidateSelectedExperience
          : [],
      country:
        Array.isArray(candidateSelectedCountry) &&
        candidateSelectedCountry.length
          ? candidateSelectedCountry
          : [],
      skill_names:
        Array.isArray(candidateSelectedSkill) && candidateSelectedSkill?.length
          ? candidateSelectedSkill
          : [],
      interview_stage:
        Array.isArray(candidateSelectedInterviewStage) &&
        candidateSelectedInterviewStage
          ? candidateSelectedInterviewStage
          : [],
    };
    setLoading(true);
    await dispatch(search_candidate_kanban_board_user_action(data));
    setLoading(false);
  };

  const onSearchInputChange = async (e) => {
    let value = e.target.value;
    if (value.length > 2) {
      setShowSearch(true);
      setSearchInputValue(value);
      onHandleSkillsReset();
      onHandleClearAllFilter();
    } else if (!value.length) {
      onSearch();
    }
  };

  const onCandidateJobsFilterChange = (item, type) => {
    const itemToAdd = item.toLowerCase();
    if (type === "skill") {
      if (!candidateSelectedSkill.includes(itemToAdd)) {
        setCandidateSelectedSkill([...candidateSelectedSkill, itemToAdd]);
        setShowSkillSearch(false);
        searchSkillInputRef.current.value = "";
      }
    }
    if (type === "employment") {
      if (!candidateSelectedEmployment.includes(itemToAdd)) {
        setCandidateSelectedEmployment([
          ...candidateSelectedEmployment,
          itemToAdd,
        ]);
      }
    }
    if (type === "experience") {
      if (!candidateSelectedExperience.includes(itemToAdd)) {
        setCandidateSelectedExperience([
          ...candidateSelectedExperience,
          itemToAdd,
        ]);
      }
    }
    if (type === "country") {
      if (!candidateSelectedCountry.includes(itemToAdd)) {
        setCandidateSelectedCountry([...candidateSelectedCountry, itemToAdd]);
      }
    }
    if (type === "interview_stage") {
      if (!candidateSelectedInterviewStage.includes(itemToAdd)) {
        setCandidateSelectedInterviewStage([
          ...candidateSelectedInterviewStage,
          itemToAdd,
        ]);
      }
      // setCandidateSelectedInterviewStage(itemToAdd);
    }
  };

  const onCandidateFilterUnselectClick = (item, type) => {
    const itemToRemove = item.toLowerCase();

    if (type === "skill") {
      if (candidateSelectedSkill.includes(itemToRemove)) {
        const filteredSkills = candidateSelectedSkill.filter(
          (item) => item !== itemToRemove
        );
        setCandidateSelectedSkill(filteredSkills);
      }
    }
    if (type === "employment") {
      if (candidateSelectedEmployment.includes(itemToRemove)) {
        const filteredSkills = candidateSelectedEmployment.filter(
          (item) => item !== itemToRemove
        );
        setCandidateSelectedEmployment(filteredSkills);
      }
    }
    if (type === "experience") {
      if (candidateSelectedExperience.includes(itemToRemove)) {
        const filteredSkills = candidateSelectedExperience.filter(
          (item) => item !== itemToRemove
        );
        setCandidateSelectedExperience(filteredSkills);
      }
    }
    if (type === "country") {
      if (candidateSelectedCountry.includes(itemToRemove)) {
        const filteredSkills = candidateSelectedCountry.filter(
          (item) => item !== itemToRemove
        );
        setCandidateSelectedCountry(filteredSkills);
      }
    }
    if (type === "interview_stage") {
      if (candidateSelectedInterviewStage.includes(itemToRemove)) {
        const filteredSkills = candidateSelectedInterviewStage.filter(
          (item) => item !== itemToRemove
        );
        setCandidateSelectedInterviewStage(filteredSkills);
        // setCandidateSelectedInterviewStage("");
      }
    }
  };
  const checkSelectedTag = (tag, type) => {
    const tagToCheck = tag.toLowerCase();
    if (type === "skill") {
      return (
        candidateSelectedSkill && candidateSelectedSkill.includes(tagToCheck)
      );
    }
    if (type === "employment") {
      return (
        candidateSelectedEmployment &&
        candidateSelectedEmployment.includes(tagToCheck)
      );
    }
    if (type === "experience") {
      return (
        candidateSelectedExperience &&
        candidateSelectedExperience.includes(tagToCheck)
      );
    }
    if (type === "country") {
      return (
        candidateSelectedCountry &&
        candidateSelectedCountry.includes(tagToCheck)
      );
    }
    if (type === "interview_stage") {
      return (
        candidateSelectedInterviewStage &&
        candidateSelectedInterviewStage.includes(tagToCheck)
      );
    }
  };

  const onHandleSkillsReset = () => {
    setCandidateSelectedSkill([]);
  };
  const resetEmployment = () => {
    setCandidateSelectedEmployment([]);
  };
  const resetExperience = () => {
    setCandidateSelectedExperience([]);
  };
  const resetCountryFilter = (e, country) => {
    setCandidateSelectedCountry([]);
  };

  const resetInterviewStage = () => {
    setCandidateSelectedInterviewStage([]);
  };

  const onHandleClearAllFilter = () => {
    onHandleSkillsReset();
    resetCountryFilter();
    resetExperience();
    resetEmployment();
    resetInterviewStage();
  };

  const onSearchSkillChange = (e) => {
    let value = e.target.value;
    if (value.length > 2) {
      setShowSkillSearch(true);
      callSkillsApi(value);
    } else if (!value.length) {
      onSkillSearch();
    }
  };

  return {
    allJobs,
    loading,
    newJobsLoading,
    candidateKanbanSearchData,
    showSearch,
    jobSearch,
    candidateSelectedEmployment,
    candidateSelectedSkill,
    candidateSelectedExperience,
    candidateSelectedCountry,
    skillsListLoading,
    skillsTagsAdded,
    candidateSelectedInterviewStage,
    showSkillSearch,
    searchInputRef,
    searchSkillInputRef,
    setLastElement,
    onViewJobDetails,
    onSearch,
    checkSelectedTag,
    onCandidateJobsFilterChange,
    checkSelectedTag,
    onCandidateFilterUnselectClick,
    onHandleSkillsReset,
    resetEmployment,
    resetExperience,
    resetCountryFilter,
    onHandleClearAllFilter,
    onSearchInputChange,
    resetInterviewStage,
    onSearchSkillChange,
    onSkillSearch,
  };
}

export default useCandidateAppliedJobs;
